import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SUPPLIER_TRADES,
  SUPPLIER_TRADE_LABELS,
  formatRef,
  requiresRegistration,
  type SupplierTrade,
} from '@promanage/core';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select, Textarea } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/maintenance/contractors/new')({
  component: NewSupplierPage,
});

/**
 * Add a contractor.
 *
 * Laid out like the owner form, because a supplier is the same kind of thing: an ENTITY that
 * holds a bank account, a GST number and a registration, with humans attached to it by role.
 * "Northline Plumbing Ltd" does not answer the phone; the plumber who turns up is a person
 * record, attached afterwards, so one human stays one record however many companies they work
 * for.
 *
 * NO BANK ACCOUNT FIELD ON THIS FORM, and that is the deliberate part. Recording where money is
 * sent is a `trust_admin` act behind a re-verified MFA (docs/SECURITY.md §5); adding a plumber is
 * ordinary property management. A field here would drag the authority for the second act down to
 * the first, on exactly the form an attacker would want to use — so the account is recorded on the
 * contractor's own record, by somebody who is allowed to, and the form says so rather than leaving
 * the operator to wonder where it went.
 */
function NewSupplierPage() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [trade, setTrade] = useState<SupplierTrade>('plumber');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [notes, setNotes] = useState('');

  const create = useMutation(
    trpc.suppliers.create.mutationOptions({
      meta: { errorTitle: 'Could not create that contractor' },
      onSuccess(created) {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.suppliers.list.queryKey() });
        // Straight to the record, not back to the list: the next thing anybody does with a new
        // contractor is attach the person who answers the phone, and that lives on the record.
        void navigate({
          to: '/maintenance/contractors/$ref',
          params: { ref: formatRef('supplier', created.refSeq) },
        });
      },
    }),
  );

  const ready = name.trim() !== '';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;

    create.mutate({
      name: name.trim(),
      trade,
      ...(registrationNumber.trim() === ''
        ? {}
        : { registrationNumber: registrationNumber.trim() }),
      ...(gstNumber.trim() === '' ? {} : { gstNumber: gstNumber.trim() }),
      ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
    });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New contractor"
        subtitle="A company or sole trader the agency engages — and pays"
        actions={
          <Link to="/maintenance/contractors">
            <Button size="sm" variant="secondary">
              All contractors
            </Button>
          </Link>
        }
      />

      <form onChange={unsaved.touch} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Panel className="flex flex-wrap items-end justify-between gap-4 p-5">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[1.375rem] font-extrabold leading-tight tracking-[-0.02em] text-ink">
              {name.trim() === '' ? <span className="text-ink-subtle">New contractor</span> : name}
            </h2>

            <div className="mt-3 max-w-lg">
              <Field
                label="Name"
                required
                hint="As it appears on their invoice — that is what a bill has to match"
              >
                {(props) => (
                  <Input
                    {...props}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Northline Plumbing Ltd"
                  />
                )}
              </Field>
            </div>
          </div>

          <div className="flex w-full flex-col items-start gap-1 sm:w-auto sm:items-end">
            <Chip tone="neutral">{SUPPLIER_TRADE_LABELS[trade]}</Chip>
            <div className="w-full sm:w-[14rem]">
              <Field label="Trade" required>
                {(props) => (
                  <Select
                    {...props}
                    value={trade}
                    onChange={(e) => setTrade(e.target.value as SupplierTrade)}
                  >
                    {SUPPLIER_TRADES.map((value) => (
                      <option key={value} value={value}>
                        {SUPPLIER_TRADE_LABELS[value]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          </div>
        </Panel>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            <Panel className="p-5">
              <PanelHeader title="The people behind it" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                A contractor has no contact details of its own. The person who answers the phone is
                a person record, attached to this company by role — so a tradesperson who works for
                two firms, or who is also a tenant somewhere, stays one record with one history.
              </p>
            </Panel>

            <Panel className="p-5">
              <PanelHeader
                title="Notes"
                subtitle="Call-out rates, after-hours cover, which suburbs they cover"
                className="p-0 pb-3"
              />
              <Textarea
                aria-label="Notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Panel>
          </div>

          <div className="flex flex-col gap-4">
            <Panel className="p-5">
              <PanelHeader title="Registration" className="p-0 pb-3" />

              <div className="flex flex-col gap-3">
                <Field
                  label="Registration number"
                  hint="EWRB for an electrician, PGDB for a plumber or gasfitter"
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={registrationNumber}
                      onChange={(e) => setRegistrationNumber(e.target.value)}
                    />
                  )}
                </Field>

                <Field
                  label="GST number"
                  hint="Leave blank if they are not registered — plenty of sole traders are not"
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={gstNumber}
                      onChange={(e) => setGstNumber(e.target.value)}
                      className="tabular-nums"
                    />
                  )}
                </Field>
              </div>

              {/*
               * A WARNING RATHER THAN A REFUSAL. An agency keying twenty existing contractors in
               * on a Tuesday has half of them on a card in a drawer, and a form that refused the
               * record would get the number invented. Unregistered gasfitting is a prosecution and
               * a declined claim, so the fact is said out loud and left on the list as a filter.
               */}
              {requiresRegistration(trade) && registrationNumber.trim() === '' ? (
                <p className="mt-3 rounded-md bg-warn-soft px-3 py-2 text-[0.75rem] font-medium text-warn">
                  {SUPPLIER_TRADE_LABELS[trade]}s must be registered to work legally in New Zealand.
                  You can save this without the number — it will show on the Contractors list as
                  missing until somebody adds it.
                </p>
              ) : null}
            </Panel>

            <Panel className="p-5">
              <PanelHeader title="Where they are paid" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                Not on this form. A bank account is recorded on the contractor’s own record by a
                trust administrator, and a new one cannot be paid for 24 hours. Redirected supplier
                invoices are the most common way money leaves an agency for the wrong account, so
                adding a contractor and deciding where money goes are two separate acts by two
                different levels of authority.
              </p>
            </Panel>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" disabled={!ready || create.isPending}>
            {create.isPending ? 'Creating' : 'Create contractor'}
          </Button>
          <Link to="/maintenance/contractors">
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
